import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ProColumns, ProTable, ActionType } from "@ant-design/pro-components";
import { Typography } from "antd";

import dayjs, { Dayjs } from "../../../config/dayjs";
import {
  clientTypeLabels,
  fileTypeLabels,
  billTypeLabels,
  fileStatusLabels,
} from "../../../constant/enum";
import { getBranchList } from "../../../general/api/branch.api";
import { IBranch } from "../../../general/type/branch.types";
import { getUser } from "../../../services/auth";
import { canUserAccessFile } from "../../../utils/filePermissions";
import { removeEmptyParams } from "../../../utils/listQueryHelper";
import { formatCompactRM } from "../../../utils/numberFormat";
import { waitTime } from "../../../utils/WaitTime";
import {
  getFileByAgentId,
  getFileByClientId,
  getFileByInternalUser,
  getFiles,
} from "../api/fileAPI";
import { IFileResponse } from "../type/file";

const { Link } = Typography;

export const FileTable = () => {
  const navigate = useNavigate();
  const actionRef = useRef<ActionType>();
  const user = getUser();
  const role = user.role;
  const id = user.id;

  const columns: ProColumns<IFileResponse>[] = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      search: false,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "Ref Number",
      dataIndex: "refNumber",
      key: "refNumber",
      render: (_, record) => {
        const canAccess = canUserAccessFile(record);

        return (
          <Link
            style={{
              color: canAccess ? "#1677ff" : "#a6a6a6",
              cursor: canAccess ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              if (canAccess) {
                navigate(`/files/${record.id}?tab=1`);
              }
            }}
          >
            {record.refNumber}
          </Link>
        );
      },
      fieldProps: {
        placeholder: "Search by ref number",
      },
    },
    {
      title: "Purchaser",
      dataIndex: "purchaser",
      key: "purchaser",
      valueType: "text",
      render: (_, record) => record.purchaser?.name ?? "-",
      fieldProps: {
        placeholder: "Search by purchaser name",
      },
      search:
        role !== "client"
          ? {
              transform: (value) => ({ purchaser: value }),
            }
          : false,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      valueType: "text",
      render: (_, record) => record.vendor?.name ?? "-",
      fieldProps: {
        placeholder: "Search by vendor name",
      },
      search:
        role !== "client"
          ? {
              transform: (value) => ({ vendor: value }),
            }
          : false,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      valueType: "text",
      render: (_, record) => record.client?.name ?? "-",
      fieldProps: {
        placeholder: "Search by client name",
      },
      search:
        role !== "client"
          ? {
              transform: (value) => ({ client: value }),
            }
          : false,
    },
    {
      title: "Agent",
      dataIndex: "agent",
      key: "agent",
      valueType: "text",
      render: (_, record) => record.agent?.name ?? "-",
      fieldProps: {
        placeholder: "Search by agent name",
      },
      search:
        role !== "agent"
          ? {
              transform: (value) => ({ agent: value }),
            }
          : false,
    },
    {
      title: "Client Type",
      dataIndex: "clientType",
      key: "clientType",
      valueType: "select",
      valueEnum: clientTypeLabels,
      search: false,
      render: (_, record) =>
        clientTypeLabels[record.clientType] ?? record.clientType,
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
      valueType: "select",
      valueEnum: fileTypeLabels,
      search: false,
      render: (_, record) => fileTypeLabels[record.fileType] ?? record.fileType,
    },
    {
      title: "Bill Type",
      dataIndex: "billType",
      key: "billType",
      valueType: "select",
      valueEnum: billTypeLabels,
      search: false,
      render: (_, record) =>
        billTypeLabels[record.billType as keyof typeof billTypeLabels] ??
        record.billType ??
        "-",
    },
    {
      title: "Property Address",
      dataIndex: "propertyAddress",
      key: "propertyAddress",
      search: false,
    },
    {
      title: "SPA/Loan/Finance/Tenancy/Contract Amount",
      dataIndex: "spaPrice",
      key: "spaPrice",
      search: false,
      sorter: true,
      render: (_, record) => formatCompactRM(record.spaPrice ?? 0),
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branchId",
      valueType: "select",
      render: (_, record) => record.branch?.branchName ?? "-",
      search: role === "admin",
      request: async () => {
        try {
          const branchData = await getBranchList();
          return (branchData.results || []).map((branch: IBranch) => ({
            label: branch.branchName,
            value: branch.id,
          }));
        } catch (error) {
          console.error("Failed to fetch branches:", error);
          return [];
        }
      },
      fieldProps: {
        showSearch: true,
        placeholder: "Search by branch name",
        filterOption: (
          input: string,
          option?: { label: string; value: string }
        ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
      },
    },
    {
      title: "Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      valueType: "select",
      valueEnum: fileStatusLabels,
      fixed: "right",
      width: 200,
      align: "center",
      search: false,
      render: (_, record) => {
        const status = record.currentStatus?.status;
        const statusLabel =
          fileStatusLabels[status as keyof typeof fileStatusLabels] ?? status;

        return (
          <Link onClick={() => navigate(`/files/${record.id}?tab=2`)}>
            {statusLabel}
          </Link>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      valueType: "dateRange",
      sorter: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt).format("DD/MM/YYYY");

        return date;
      },
      search: {
        transform: (value) => {
          if (!value || !Array.isArray(value) || value.length !== 2) {
            return undefined;
          }
          const [start, end] = value;
          if (!start || !end) {
            return undefined;
          }
          const toDay = (v: any) => {
            if (dayjs.isDayjs(v)) return v as Dayjs;
            const parsedStrict = dayjs(v, "DD/MM/YYYY", true);
            return parsedStrict.isValid() ? parsedStrict : dayjs(v);
          };

          const startDay = toDay(start);
          const endDay = toDay(end);
          if (!startDay.isValid() || !endDay.isValid()) {
            return undefined;
          }
          // Set end date to end of day to include the full day
          const endOfDay = endDay.endOf("day");
          return {
            createdAt: {
              start: startDay.startOf("day").toISOString(),
              end: endOfDay.toISOString(),
            },
          };
        },
      },
      fieldProps: {
        placeholder: ["Start Date", "End Date"],
        className: "createdAtSearch",
        format: "DD/MM/YYYY",
      },
    },
  ];

  return (
    <>
      <ProTable<IFileResponse>
        cardBordered
        form={{ layout: "vertical" }}
        scroll={{ x: "max-content" }}
        request={async (params, sort, filter) => {
          try {
            params = removeEmptyParams(params);
            if (role === "admin") {
              const data = await getFiles({ filter, sort, params });
              const files = data.results;

              return {
                data: files,
                success: true,
                total: data.totalResults,
              };
            } else if (role === "client") {
              const data = await getFileByClientId(id, {
                filter,
                sort,
                params,
              });
              const files = data.results;

              return {
                data: files,
                success: true,
                total: data.totalResults,
              };
            } else if (role === "agent") {
              const data = await getFileByAgentId(id, {
                filter,
                sort,
                params,
              });
              const files = data.results;

              return {
                data: files,
                success: true,
                total: data.totalResults,
              };
            } else {
              const data = await getFileByInternalUser(id, {
                filter,
                sort,
                params,
              });
              const files = data.results;

              return {
                data: files,
                success: true,
                total: data.totalResults,
              };
            }
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 10,
            };
          }
        }}
        rowKey={(data) => data.id}
        columns={columns}
        editable={{
          type: "multiple",
          onSave: async (_rowKey, _data, _row) => {
            await waitTime(2000);
          },
        }}
        actionRef={actionRef}
      />
    </>
  );
};
